import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock } from 'lucide-react';
import { Image } from '@/components/ui/image';
import type { SpeechTopicDetail } from '@shared/api.interface';
import {
  CONTENT_CATEGORY_LABELS,
  SUITABLE_GRADE_LABELS,
  CONTENT_CATEGORY_COLORS,
  SUITABLE_GRADE_COLORS,
} from '@shared/api.interface';

interface TopicDetailDialogProps {
  topic: SpeechTopicDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TopicDetailDialog({
  topic,
  open,
  onOpenChange,
}: TopicDetailDialogProps) {
  if (!topic) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl">{topic.title}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="px-6 space-y-6">
            {/* 封面图 */}
            {topic.cover_image && (
              <div className="relative aspect-video w-full overflow-hidden rounded-xl">
                <Image
                  src={topic.cover_image}
                  alt={topic.title}
                  className="h-full w-full object-cover"
                  sizes="(max-width: 672px) 100vw, 672px"
                />
              </div>
            )}

            {/* 标签信息 */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="secondary"
                className={`${CONTENT_CATEGORY_COLORS[topic.content_category].bg} ${CONTENT_CATEGORY_COLORS[topic.content_category].text} border-0`}
              >
                {CONTENT_CATEGORY_LABELS[topic.content_category]}
              </Badge>
              <Badge
                variant="secondary"
                className={`${SUITABLE_GRADE_COLORS[topic.suitable_grade].bg} ${SUITABLE_GRADE_COLORS[topic.suitable_grade].text} border-0`}
              >
                {SUITABLE_GRADE_LABELS[topic.suitable_grade]}
              </Badge>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {topic.estimated_duration}
              </span>
            </div>

            {/* 视频展示 */}
            {topic.video_display && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">视频展示</h4>
                <div className="aspect-video w-full rounded-lg bg-muted flex items-center justify-center">
                  <video
                    src={topic.video_display}
                    controls
                    className="max-h-full max-w-full rounded-lg"
                  >
                    您的浏览器不支持视频播放
                  </video>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* 底部操作区 */}
        <div className="border-t p-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            关闭
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
